// ****************** cicero.js ******************
// Cicero API, agnostic to rendering grammar.
// Author: Hyeok Kim @ Adobe AEL
// Dependency: D3.js (v6) for json file control, underscore.js for equality

// TODO: resolve conflicts. only pass "consistent/stable" spec to grammar-specific compiler & renderer

let ciceroPath = "cicero.json"

import * as d3 from 'd3';
import * as _ from 'underscore';

class Cicero {
  // Cicero Object  
  constructor(name, sourceVis, config, description) {
    this.spec = {
      "$schema": ciceroPath,
      "name": name,
      "transformations": []
    };
    this.name = name;
    // if not provided just undefined
    this.sourceVis = sourceVis;
    this.spec.config = config || {};
    this.spec.description = description || "This is a Cicero transformation spec for " + name + ".";
    // compiler;
    this.compiler = {};
  }
  setCompiler(comp, rend) {
    // register a compiler @comp and renderer @rend (all should be Function-type)
    let self = this;
    if (rend === undefined && comp !== undefined && comp.constructor.name === "Object") {
      // when provided as {compile: Function, render: Function}
      if (comp.compile !== undefined && typeof comp.compile == "function") {
        self.compiler.compile = comp.compile;
      } else {
        console.warn("A non-function compiler is ignored.");
      }
      if (comp.render !== undefined && typeof comp.render == "function") {
        self.compiler.render = comp.render;
      } else {
        console.warn("A non-function renderer is ignored.");
      }
    } else {
      // when provided as (Function|undefined, Function|undefined)
      if (comp !== undefined && typeof comp == "function") {
        self.compiler.compile = comp;
      } else {
        console.warn("A non-function compiler is ignored.");
      }
      if (rend !== undefined && typeof rend == "function") {
        self.compiler.render = rend;
      } else {
        console.warn("A non-function renderer is ignored.");
      }
    }
  }
  setSource(sourceVis) {
    // set source visualization @sourceVis separately
    this.sourceVis = sourceVis;
  }
  setConfig(config, config2) {
    // set source visualization @config (@config2) separately
    if (config.constructor.name === "Object") {
      // when provided as {key: value}
      Object.keys(config).forEach(key => {
        this.spec.config[key] = config[key];
      })
    } else if (config.constructor.name === "String" && config2 !== undefined) {
      // when provided as (key, value)
      this.spec.config[config] = config2;
    }
  }
  addTransformation(item) {
    // add a transformation
    if (item.constructor.name === "Array") { // multiple
      let self = this;
      item.forEach(it => {
        self.spec.transformations.push(it)
      });
    } else if (item.constructor.name === "Object") { // single
      this.spec.transformations.push(item);
    }
  }
  removeTransformation(item) {
    // remove a transformation given @item; return the list of removed transformation rules.
    let flag = false;
    let self = this;
    if (item.constructor.name === "Number") { // given as a single index
      item = [item];
      flag = true;
    } else if (item.constructor.name === "Object") { // given as a single rule object
      item = [self.getTransformationIndex(item)];
      flag = true;
    } else if (item.constructor.name === "Array" && item.every(d => { return d.constructor.name === "Object" })) { // given as an array of rule objects
      item = item.map(it => { return self.getTransformationIndex(it) });
      flag = true;
    } else if (item.constructor.name === "Array" && item.every(d => { return d.constructor.name === "Number" })) { // given as an array of indices 
      flag = true;
    }
    let removed = [];
    if (flag) {
      removed = self.spec.transformations.filter((d, i) => { return item.includes(i) });
      self.spec.transformations = self.spec.transformations.filter((d, i) => { return !item.includes(i) });
    }
    return removed;
  }
  getTransformationIndex(item) {
    // get the index of transformation @item
    return self.spec.transformations.filter(rule => { return _.isEqual(rule, item) })[0];
  }
  getTransformations(deepcopy = true) {
    // return the list of transformations (@deepcopy = true by default)
    let self = this;
    if (deepcopy) return JSON.parse(JSON.stringify(self.spec.transformations));
    else return self.spec.transformations;
  }
  getSpec(deepcopy = true) {
    // return the full specification (@deepcopy = true by default)
    let self = this;
    if (deepcopy) return JSON.parse(JSON.stringify(self.spec));
    else return self.spec;
  }
  async getTransformed(config={}) {
    // return the compiled transformed view
    if (["Function", "AsyncFunction"].includes(this.compiler.compile?.constructor.name)) {
      let spec = this.spec;
      let sourceSpec = this.sourceVis;
      return await this.compiler.compile(spec, sourceSpec);
    } else if (this.compiler.compile === undefined) {
      console.warn("A compiler is not provided.");
      return undefined;
    } else {
      console.warn("The provided compiler is not a Function.", this.compiler.compile);
      return undefined;
    }
  }
  async getRendered(placeSelector, config={}) {
    // compile and render the compiled transformed view at @placeSelector
    if (typeof this.compiler.compile == "function" && typeof this.compiler.render == "function") {
      // when both compiler and renderer are proper functions
      let spec = this.spec;
      let sourceSpec = this.sourceVis;
      let compiled;
      if (this.compiler.compile.constructor.name === "AsyncFunction") compiled = await this.compiler.compile(spec, sourceSpec);
      else compiled = this.compiler.compile(spec, sourceSpec);
      return {
        compiled: compiled,
        rendered: this.compiler.render(placeSelector, compiled, config)
      };
    } else {
      // otherwise
      let warnings = []
      if (this.compiler.compile === undefined) {
        warnings.push("A compiler is not provided.");
      } else {
        warnings.push("The provided compiler is not a Function.");
      }
      if (this.compiler.render === undefined) {
        warnings.push("A renderer is not provided.");
      } else {
        warnings.push("The provided renderer is not a Function.");
      }
      console.warn(warnings.join(" "), this.compiler);
      return null;
    }
  }
}

async function loadCicero(specPath, sourceVis, compiler, renderer) {
  // load a Cicero spec with spec file path @specPath and optional compiler @compiler and renderer @rend
  // @compiler & @renderer follow the polymorphism of the Cicero class's setCompiler method.
  // Returns a Cicero Object
  let spec = await fetch(specPath);
  if (!spec) return URIError("Wrong Cicero file path!");
  let data = await spec.json();
  
  let ciceroObj = new Cicero(data.name, sourceVis, data.config, data.description);
  ciceroObj.addTransformation(data.transformations);
  ciceroObj.setCompiler(compiler, renderer);
  return ciceroObj
}

async function loadCiceroFromSpec(data, sourceVis, compiler, renderer) {
  // load a Cicero spec with spec file path @specPath and optional compiler @compiler and renderer @rend
  // @compiler & @renderer follow the polymorphism of the Cicero class's setCompiler method.
  // Returns a Cicero Object
  let ciceroObj = new Cicero(data.name, sourceVis, data.config, data.description);
  ciceroObj.addTransformation(data.transformations);
  ciceroObj.setCompiler(compiler, renderer);
  return ciceroObj
}

let CCR = {};

CCR.toFilterString = function (fObj) {
  // const operators = ["eq", "neq", "leq", "geq", "lt", "gt", "range", "xrange", "startsWith", "endsWith", "startsEndsWith", "includes", "pattern", "oneOf"];
  function makeAtomicStatement(fieldname, elem, op) {
    if (op === "eq") {
      if (typeof elem == "string") return "datum." + fieldname + " == '" + elem.toString() + "'";
      else return "datum." + fieldname + " == " + elem.toString() + "";
    } else if (op === "neq") {
      if (typeof elem == "string") return "datum." + fieldname + " != '" + elem.toString() + "'";
      else return "datum." + fieldname + " != " + elem.toString() + "";
    } else if (op === "oneOf") {
      return "oneOf(datum." + fieldname + ", [" + elem.map(d => (typeof d == "string" ? "'" + d + "'" : d.toString())).join(", ") + "])";
    } else if (op === "leq") {
      return "datum." + fieldname + " <= " + elem.toString();
    } else if (op === "geq") {
      return "datum." + fieldname + " >= " + elem.toString();
    } else if (op === "lt") {
      return "datum." + fieldname + " < " + elem.toString();
    } else if (op === "gt") {
      return "datum." + fieldname + " > " + elem.toString();
    } else if (op === "range") {
      let min = (elem[0] > elem[1] ? elem[1] : elem[0]),
        max = (elem[0] < elem[1] ? elem[1] : elem[0]);
      return "(datum." + fieldname + " >= " + min.toString() + " && datum." + fieldname + " <= " + max.toString() + ")";
    } else if (op === "xrange") {
      let min = (elem[0] > elem[1] ? elem[1] : elem[0]),
        max = (elem[0] < elem[1] ? elem[1] : elem[0]);
      return "(datum." + fieldname + " > " + min.toString() + " && datum." + fieldname + " < " + max.toString() + ")";
    } else if (op === "startsWith") {
      return "startsWith(datum." + fieldname + ", '" + elem + "')";
    } else if (op === "endsWith") {
      return "endsWith(datum." + fieldname + ", '" + elem + "')";
    } else if (op === "startsEndsWith") {
      return "startsEndsWith(datum." + fieldname + ", '" + elem[0] + ", " + elem[1] + "')";
    } else if (op === "includes") {
      return "includes(datum." + fieldname + ", '" + elem + "')";
    } else if (op === "pattern") {
      return "pattern(datum." + fieldname + ", /" + elem + "/g)";
    }
  }

  function makeSingleStatement(fieldname, object) {
    if (fieldname == null) fieldname = "value"
    if (object.constructor.name === "Object" && object.not !== undefined) {
      return "!" + makeSingleStatement(fieldname, object.not);
    } else {
      if (object.constructor.name === "Array") { // list of operators
        return "(" + object.map(item => {
          return makeSingleStatement(fieldname, item);
        }).join(" || ") + ")";
      } else if (object.constructor.name === "Object") { // list of operators
        return "(" + Object.keys(object).map(op => {
          return makeAtomicStatement(fieldname, object[op], op);
        }).join(" && ") + ")";
      } else { // a value
        return makeAtomicStatement(fieldname, object, "eq");
      }
    }
  }

  if (fObj.constructor.name === "Object") {
    // first-level: fields;
    return "(" + Object.keys(fObj).map(fieldname => {
      return makeSingleStatement(fieldname, fObj[fieldname]);
    }).join(" && ") + ")"
  } else if (fObj.constructor.name === "Array") {
    return "(" + fObj.map(d => CCR.toFilterString(d)).join(" || ") + ")"
  } else {
    return makeSingleStatement(null, fObj)
  }
}

CCR.makeFilterFunction = function (fObj, isAtomic) {
  let filterState;
  if (fObj.constructor.name === "Object") filterState = CCR.toFilterString(fObj);
  else if (fObj.constructor.name === "Array") filterState = CCR.toFilterString(fObj);
  else if (fObj.constructor.name === "String") filterState = fObj;

  return function (datum) {
    // pass a "single" datum row
    let preEval = `function startsWith(val, str) {
      return val.startsWith(str);
    }
    function endsWith(val, str) {
      return val.endsWith(str);
    }
    function startsEndsWith(val, start, end) {
      return val.startsWith(start) && val.endsWith(end);
    }
    function includes(val, str) {
      return val.includes(str);
    }
    function pattern(val, expr) {
      return val.match(expr).length > 0;
    }
    function oneOf(val, arr) {
      return arr.includes(val);
    }`
    if (isAtomic) return eval(preEval + filterState.replace(/datum\.value/g, JSON.stringify(datum)))
    else return eval(preEval + filterState.replace(/datum/g, JSON.stringify(datum)))
  }
}

CCR.optionOperation = function (o, n) {
  if (n.constructor.name === "Object") {
    if (n.by !== undefined) {
      return o + n.by;
    } else if (n.prod !== undefined) {
      return o * n.prod;
    }
    return n;
  } else {
    return n;
  }
}

export { ciceroPath, Cicero, loadCicero, loadCiceroFromSpec, CCR };